import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";
import { message } from "antd";

export const initialState = {
	loading: false,
	error: null,
	distributors: [],
	currentDistributor: null,
};

export const createDistributor = createAsyncThunk("distributor/createDistributor", async (distributorData, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post("distributor/create", distributorData);
		message.success("Distributor created successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to create distributor");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const fetchDistributors = createAsyncThunk(
	"distributor/fetchDistributors",
	async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams({
				page,
				limit,
				...filters,
			});
			const response = await axiosHttp.get(`distributor/list?${params}`);
			return response.data;
		} catch (error) {
			message.error(error.response?.data?.message || "Failed to fetch distributors");
			return rejectWithValue(error.response?.data || error.message);
		}
	},
);

export const updateDistributor = createAsyncThunk("distributor/updateDistributor", async ({ distributorId, data }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.patch(`distributor/update?id=${distributorId}`, data);
		message.success("Distributor updated successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to update distributor");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const deleteDistributor = createAsyncThunk("distributor/deleteDistributor", async ({ distributorId, territoryId }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`distributor/delete/${distributorId}/${territoryId}`);
		message.success("Distributor removed from territory successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to remove distributor from territory");
		return rejectWithValue(error.response?.data || error.message);
	}
});

const distributorSlice = createSlice({
	name: "distributor",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearCurrentDistributor: (state) => {
			state.currentDistributor = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(createDistributor.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createDistributor.fulfilled, (state, action) => {
				state.loading = false;
				state.currentDistributor = action.payload.data;
			})
			.addCase(createDistributor.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(fetchDistributors.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchDistributors.fulfilled, (state, action) => {
				state.loading = false;
				state.distributors = action.payload.data?.docs || [];
			})
			.addCase(fetchDistributors.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(updateDistributor.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateDistributor.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(updateDistributor.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(deleteDistributor.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteDistributor.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(deleteDistributor.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});
	},
});

export const { clearError, clearCurrentDistributor } = distributorSlice.actions;

export default distributorSlice.reducer;
